import { Button, Text, TextInput, Section, Box } from '@acast/decibel';
import { createShow } from 'ducks/shows';
import T from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { presentModal } from 'services/modals';
import Field from 'shared/components/field';
import Modal from 'shared/components/modal';
import isValidPermalink from 'shared/helpers/is-valid-permalink';
import navigateTo from 'shared/helpers/navigate-to';
import Container from 'shared/components/content-container';

const ACAST_BASE_URL = 'https://www.acast.com/';

const renderGenericError = () => (
  <Modal
    title="Something went wrong..."
    dialog="Something went wrong. Try again in a little while. If the problem persists, please contact support."
    actions={Modal.Actions.OK}
  />
);

class NewShowPage extends React.Component {
  static propTypes = {
    createShow: T.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      errors: {},
      fields: {
        name: '',
        permalink: '',
      },
      loading: false,
    };
  }

  handleChange = key => value => {
    const { errors, fields } = this.state;

    this.setState({ fields: { ...fields, [key]: value } });

    if (errors[key]) {
      delete errors[key];
      this.setState({ errors });
    }
  };

  validate = () => {
    const errors = {};
    const { name, permalink } = this.state.fields;

    if (!name) {
      errors.name = 'Please enter a name for your show.';
    }

    if (!permalink) {
      errors.permalink = 'Please enter a URL for your show.';
    } else if (!isValidPermalink(permalink)) {
      errors.permalink = 'This URL contains invalid characters.';
    }

    this.setState({ errors });

    return Object.keys(errors).length === 0;
  };

  presentConfirmModal = e => {
    e.preventDefault();

    if (!this.validate()) return;

    const { fields } = this.state;

    Modal.present({
      title: 'Confirm show URL',
      dialog: (
        <>
          <Text>You have chosen the following show URL:</Text>
          <Text>{ACAST_BASE_URL + fields.permalink}</Text>
          <Text mb={0}>
            Once your new show has been created, you will not be able to edit your show URL. Please
            confirm and click CREATE to continue.
          </Text>
        </>
      ),
      actions: [
        { name: 'Cancel', value: false, variant: 'tertiary' },
        { name: 'Create', value: true },
      ],
    }).then(isConfirmed => {
      if (isConfirmed) {
        this.setState({ loading: true });
        this.handleSubmit();
      }
    });
  };

  handleSubmit = () => {
    const show = {
      name: this.state.fields.name,
      url: this.state.fields.permalink,
    };

    return this.props
      .createShow(show)
      .then(response => {
        this.setState({ loading: false });
        navigateTo(`/shows/${response.id}`);
      })
      .catch(err => {
        switch (err.status) {
          case 409:
            this.setState({ errors: { permalink: 'This URL is not available.' } });
            break;

          default:
            presentModal(renderGenericError());
            break;
        }

        this.setState({ loading: false });
      });
  };

  handleCancel = () => {
    navigateTo('/overview');
  };

  render() {
    const { errors, fields, loading } = this.state;

    return (
      <Container>
        <Section>
          <Text variant="h3">Create Show</Text>
          <Box mb={4} flexDirection="column">
            <Field label="Show title" meta={{ error: errors.name, touched: true }}>
              <TextInput
                value={fields.name}
                invalid={!!errors.name}
                onChange={event => this.handleChange('name')(event.target.value)}
              />
            </Field>
          </Box>
          <Field
            label="Show URL"
            subtitle={() => (
              <span>
                Your show will be available at {ACAST_BASE_URL}
                <b>{fields.permalink || 'show-url'}</b>.
              </span>
            )}
            meta={{ error: errors.permalink, touched: true }}
          >
            <TextInput
              value={fields.permalink}
              invalid={!!errors.permalink}
              onChange={event => this.handleChange('permalink')(event.target.value)}
            />
          </Field>
          <Box gap={4} mt={4} justifyContent="flex-end">
            <Button variant="tertiary" onClick={this.handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.presentConfirmModal}
              disabled={loading}
              busy={loading}
            >
              Create
            </Button>
          </Box>
        </Section>
      </Container>
    );
  }
}

export default connect(
  () => ({}),
  { createShow }
)(NewShowPage);
